import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Siren, Plus, Pencil, Trash2, MapPin, Mail, CalendarDays, ListChecks, FileEdit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SpecialSituationsCalendar } from '../components/settings/SpecialSituationsCalendar';
import { confirmDialog } from '../components/ConfirmHost';
import { SituationFormDialog } from '../components/settings/SituationFormDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errorText = (err) => (typeof err.response?.data?.detail === 'string' ? err.response.data.detail : err.response?.data?.detail?.[0]?.msg?.replace('Value error, ', '') || 'İşlem başarısız');

const SituationCard = ({ item, locations, onEdit, onDelete }) => {
  const locNames = item.location_ids.map(id => locations.find(l => l.id === id)?.name).filter(Boolean);
  return (
    <Card className={`border-slate-200 transition-shadow hover:shadow-md ${item.is_active ? '' : 'opacity-60'}`} data-testid={`situation-card-${item.id}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 flex items-center gap-2" data-testid={`situation-title-${item.id}`}>
              {item.title}
              {!item.is_active && <Badge variant="outline" className="text-xs">Pasif</Badge>}
            </p>
            {item.description && <p className="text-sm text-slate-500 mt-0.5">{item.description}</p>}
          </div>
          <div className="flex gap-1 shrink-0">
            <Button size="icon" variant="ghost" className="text-blue-600" onClick={() => onEdit(item)} data-testid={`situation-edit-${item.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => onDelete(item)} data-testid={`situation-delete-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid={`situation-locations-${item.id}`}>
          <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
          {locNames.length ? locNames.map(n => <Badge key={n} variant="secondary" className="font-normal">{n}</Badge>)
            : <span className="text-sm italic text-amber-600">Lokasyon atanmadı, hiçbir talepte görünmez</span>}
        </div>
        <div className="text-sm text-slate-600 space-y-0.5" data-testid={`situation-emails-${item.id}`}>
          <p className="flex gap-2"><Mail className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" /><span><b>TO:</b> {item.email_to || '-'}</span></p>
          <p className="pl-6"><b>CC:</b> {item.email_cc || '-'}</p>
        </div>
        <p className="text-xs text-slate-400">{item.permit_count} talepte seçildi</p>
      </CardContent>
    </Card>
  );
};

export default function SpecialSituationsPage() {
  const { user, canView, canTab } = useAuth();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = () => axios.get(`${API}/special-situations`).then(r => setItems(r.data)).catch(() => toast.error('Özel durumlar yüklenemedi'));
  useEffect(() => {
    if (!canView('special_situations')) return;
    load();
    axios.get(`${API}/locations`).then(r => setLocations(r.data)).catch(() => {});
  }, [user]);

  const save = async (data) => {
    try {
      if (editing?.id) await axios.put(`${API}/special-situations/${editing.id}`, data);
      else await axios.post(`${API}/special-situations`, data);
      toast.success(editing?.id ? 'Özel durum güncellendi' : 'Özel durum eklendi');
      setEditing(null);
      load();
    } catch (err) { toast.error(errorText(err)); }
  };

  const remove = async (item) => {
    if (!(await confirmDialog({ title: 'Özel durum silinsin mi?', description: `"${item.title}" silinecek. Daha önce seçildiği talepler etkilenmez.`, confirmLabel: 'Evet, Sil' }))) return;
    try {
      await axios.delete(`${API}/special-situations/${item.id}`);
      toast.success('Özel durum silindi');
      load();
    } catch (err) { toast.error(errorText(err)); }
  };

  if (!canView('special_situations')) return <p className="text-slate-500" data-testid="special-situations-forbidden">Bu sayfa sadece Süper Admin kullanıcılar içindir.</p>;

  return (
    <div className="space-y-6 w-full" data-testid="special-situations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Siren className="h-6 w-6 text-amber-600" />Özel Durumlar</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">Çalışmaya bağlı özel durumları (ör. gürültülü çalışma) tanımlayın ve lokasyonlara atayın. Firma talep oluştururken Bölüm 1'in sonunda bu durumları işaretleyebilir; talep onaylanınca ilgili e-posta grubuna bildirim gider.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/system-settings?tab=bildirimler')} data-testid="situation-template-btn"><FileEdit className="h-4 w-4 mr-1" />E-posta Şablonunu Düzenle</Button>
          <Button onClick={() => setEditing({})} className="bg-slate-900 hover:bg-slate-800" data-testid="situation-add-btn"><Plus className="h-4 w-4 mr-1" />Yeni Özel Durum</Button>
        </div>
      </div>
      <Tabs defaultValue={canTab('special_situations', 'list') ? 'list' : 'calendar'} className="space-y-4">
        <TabsList data-testid="situation-tabs">
          {canTab('special_situations', 'list') && <TabsTrigger value="list" data-testid="situation-tab-list"><ListChecks className="h-4 w-4 mr-1" />Tanımlar</TabsTrigger>}
          {canTab('special_situations', 'calendar') && <TabsTrigger value="calendar" data-testid="situation-tab-calendar"><CalendarDays className="h-4 w-4 mr-1" />Takvim</TabsTrigger>}
        </TabsList>
        <TabsContent value="calendar"><SpecialSituationsCalendar situations={items} locations={locations} /></TabsContent>
        <TabsContent value="list">
      {items.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-slate-500" data-testid="situations-empty">Henüz özel durum eklenmedi.</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="situations-list">
          {items.map(it => <SituationCard key={it.id} item={it} locations={locations} onEdit={setEditing} onDelete={remove} />)}
        </div>
      )}
        </TabsContent>
      </Tabs>
      <SituationFormDialog open={!!editing} item={editing} locations={locations} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}
